import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import VerificationSDK from "../VerificationSDK";
import "./kycModal.css";

interface KycInitFormProps {
  sdk?: VerificationSDK;
  serverKey?: string;
  defaultValues?: {
    reference?: string;
    email?: string;
    callback_url?: string;
    redirect_url?: string;
  };
  onStarted?: (sdk: VerificationSDK) => void;
  qrOnlyMode?: boolean;
  hideCameraPreview?: boolean;
  onFaceCompleted?: () => void;
  pollIntervalMs?: number;
}

export const KycInitForm: React.FC<KycInitFormProps> = ({
  sdk: providedSdk,
  serverKey,
  defaultValues,
  onStarted,
  qrOnlyMode,
  hideCameraPreview,
  onFaceCompleted,
  pollIntervalMs = 20000,
}) => {
  const [reference, setReference] = useState(defaultValues?.reference || "");
  const [email, setEmail] = useState(defaultValues?.email || "");
  const [callbackUrl, setCallbackUrl] = useState(
    defaultValues?.callback_url || ""
  );
  const [redirectUrl, setRedirectUrl] = useState(
    defaultValues?.redirect_url || ""
  );
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [polling, setPolling] = useState(false);
  const pollingRef = useRef<number | null>(null);

  const sdk = providedSdk || new VerificationSDK({ serverKey: serverKey || "" });

  const uiDefaults =
    sdk && (sdk as any).getUiConfig ? (sdk as any).getUiConfig() : {};
  const styles = uiDefaults.styles || {};
  const classNames = uiDefaults.classNames || {};

  // Stop camera and polling on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (pollingRef.current) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("[KYC] onSubmit triggered");

    // sdk is always available (constructed with empty key if none provided)
    setLoading(true);

    try {
      // Initialize session with user-provided values
      const res = await sdk.init({
        reference: reference.trim(),
        email: email.trim(),
        callback_url: callbackUrl.trim(),
        redirect_url: redirectUrl.trim(),
        device_type: "WEB",
      });
      const sessionIdFromRes = (res as any)?.data?.session_id as string | undefined;
      if (!sessionIdFromRes) throw new Error("Init succeeded but session_id missing");

      try { localStorage.setItem("kyc_session_id", sessionIdFromRes); } catch {}
      if ((sdk as any).setSessionId) { (sdk as any).setSessionId(sessionIdFromRes); }
      setSessionId(sessionIdFromRes);

      // Move directly to face scan step (showCamera = true)
      setShowCamera(true);
      setQrCode(null);
      setPolling(false);

      onStarted?.(sdk);
    } catch (err: any) {
      toast.error(err?.message || "Failed to initialize KYC session");
    } finally {
      setLoading(false);
    }
  };

  // If QR-only mode is requested, auto-init without showing the form
  useEffect(() => {
    const autoStart = async () => {
      if (!qrOnlyMode) return;
      // sdk is always available
      // Use defaults from fields (already in state)
      if (!reference || !email || !callbackUrl || !redirectUrl) return; // wait till defaults are in state
      if (qrCode) return; // already started
      setLoading(true);
      try {
        const res = await sdk.init({
          reference: reference.trim(),
          email: email.trim(),
          callback_url: callbackUrl.trim(),
          redirect_url: redirectUrl.trim(),
          device_type: "WEB",
        });
        const sid = (res as any)?.data?.session_id as string | undefined;
        if (!sid) throw new Error("Init succeeded but session_id missing");
        try { localStorage.setItem("kyc_session_id", sid); } catch {}
        if ((sdk as any).setSessionId) { (sdk as any).setSessionId(sid); }
        setSessionId(sid);
        const keyParam = (serverKey && serverKey.length) ? `&key=${encodeURIComponent(serverKey)}` : "";
        const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
        const mobileUrl = `${origin}/mobile-kyc/${encodeURIComponent(sid)}?mode=face${keyParam}`;
        setQrCode(mobileUrl);
        setPolling(true);
        pollingRef.current = window.setInterval(async () => {
          try {
            const status: any = await sdk.getStatus(sdk?.getServerKey());
            const data: any = status?.data ?? status;
            const faceStatus: string | undefined = data?.face_recognition_status || data?.face_status || data?.face || data?.faceRecognitionStatus;
            if (typeof faceStatus === "string" && faceStatus.toUpperCase() === "COMPLETED") {
              if (pollingRef.current) {
                window.clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
              setPolling(false);
              onFaceCompleted?.();
            }
          } catch {}
        }, Math.max(1000, pollIntervalMs));
        onStarted?.(sdk);
      } catch (err: any) {
        toast.error(err?.message || "Failed to initialize KYC session");
      } finally {
        setLoading(false);
      }
    };
    autoStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrOnlyMode, reference, email, callbackUrl, redirectUrl, sdk]);

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", zIndex: 1000 }}>
      {!qrCode && !qrOnlyMode ? (
        <form
          onSubmit={onSubmit}
          className={classNames?.container || ""}
          style={styles?.container || {}}
        >
          <h2 className={classNames?.title || "kyc-title"} style={styles?.title}>
            Start KYC
          </h2>


          <div style={{ display: "grid", gap: 8 }}>
            <input
              className={classNames?.input || "kyc-input"}
              style={styles?.input}
              placeholder="Reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
            <input
              className={classNames?.input || "kyc-input"}
              style={styles?.input}
              placeholder="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              className={classNames?.input || "kyc-input"}
              style={styles?.input}
              placeholder="Callback URL"
              value={callbackUrl}
              onChange={(e) => setCallbackUrl(e.target.value)}
            />
            <input
              className={classNames?.input || "kyc-input"}
              style={styles?.input}
              placeholder="Redirect URL"
              value={redirectUrl}
              onChange={(e) => setRedirectUrl(e.target.value)}
            />
          </div>

          <button
            type="submit"
            className={classNames?.input || "kyc-input"}
            style={{
              ...(styles?.input || {}),
              marginTop: 10,
              background: "linear-gradient(90deg, #FF8A00 0%, #FF3D77 100%)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: 10,
              cursor: "pointer",
            }}
            disabled={
              loading || !reference || !email || !callbackUrl || !redirectUrl
            }
          >
            {loading ? "Starting..." : "Start KYC"}
          </button>
        </form>
      ) : (
        <div style={{ marginTop: 20 }}>
          <div style={{ display: "grid", gap: 12 }}>
            {!hideCameraPreview && (
              <div>
                <h3 style={{ margin: "0 0 8px 0" }}>Camera</h3>
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  style={{ width: "100%", borderRadius: 8, background: "#000", aspectRatio: "4/3" }}
                />
              </div>
            )}
            <div style={{ textAlign: "center" }}>
              <h3 style={{ margin: "8px 0" }}>Scan on Mobile to Continue</h3>
              {/* Render QR image based on mobile link using a public encoder */}
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrCode || "")}`}
                alt="KYC QR Code"
                style={{
                  maxWidth: 220,
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  padding: 5,
                }}
              />
              {sessionId && (
                <p style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
              Or open: {`${(typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : ''}/mobile-kyc/${sessionId}?mode=face${serverKey ? `&key=${encodeURIComponent(serverKey)}` : ""}`}
                </p>
              )}
              {polling && (
                <p style={{ fontSize: 12, color: "#1f6feb", marginTop: 8 }}>
                  Waiting for face capture to complete on mobile...
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
